'use client';

import DOMPurify from 'dompurify';
import {useEffect} from 'react';

// NOTE: Keep sorted.
const ALLOWLIST: Array<string | RegExp> = [
  'https://accounts.google.com/gsi/client',
  'https://bat.bing.com/',
  'https://bat.bing.net/',
  'https://connect.facebook.net/',
  'https://googlefibersurveys.qualtrics.com',
  'https://iad1.qualtrics.com',
  'https://maps.googleapis.com/maps',
  'https://pay.google.com/gp/p/js/pay.js',
  'https://payments.google.com/payments/v4/js/integrator.js',
  'https://payments.sandbox.google.com/payments/v4/js/integrator.js',
  'https://siteintercept.qualtrics.com',
  'https://www.google.com/recaptcha',
  'https://www.google.com/recaptcha/api.js',
  'https://www.googletagmanager.com/debug/bootstrap',
  'https://www.googletagmanager.com/gtm.js',
  'https://www.gstatic.com/recaptcha/releases',
  'https://zn1eim26wxocbgvz7-googlefibersurveys.siteintercept.qualtrics.com/SIE/',
  /^https:\/\/([a-zA-Z0-9-]+\.)*(pure\.cloud|genesys\.cloud)\//,
  /^https:\/\/([a-zA-Z0-9-]+\.)*clarity\.ms\//,
];

interface TrustedTypesWindow {
  trustedTypes?: {
    defaultPolicy?: unknown;
    createPolicy: (
      name: string,
      rules: {
        createHTML?: (dirty: string) => string;
        createScriptURL?: (dirtyUrl: string) => string;
        createScript?: (inlineScript: string) => string;
      },
    ) => unknown;
  };
}

export function TrustedTypes() {
  useEffect(() => {
    const ttWindow = window as unknown as TrustedTypesWindow;
    if (ttWindow.trustedTypes?.defaultPolicy) return;
    if (typeof window !== 'undefined' && ttWindow.trustedTypes?.createPolicy) {
      try {
        ttWindow.trustedTypes.createPolicy('default', {
          createHTML: (dirty: string) => {
            // Genesys widget relies on injecting iframes and some inline scripts like `<script>document.F=Object</script>`
            // DOMPurify strips these by default, silently breaking the widget.

            // Exact regex match for the one known inline script snippet Genesys requires to acquire a sandbox reference
            // This aligns with Genesys without being tied to specific minified variable names.
            const trimmed = dirty.trim();
            const safeInlineScriptRegex =
              /^<script>(?:document|window)\.[a-zA-Z_][a-zA-Z0-9_]*\s*=\s*(?:Object|Array|String|Number|Boolean|Function);?<\/script>$/i;
            if (safeInlineScriptRegex.test(trimmed)) {
              return dirty;
            }

            const baseConfig = {RETURN_TRUSTED_TYPE: false as const};

            // If it appears to be a genesys widget HTML container injection, give it more leeway for iframes/styles,
            // but still sanitize it thoroughly with DOMPurify.
            const config = trimmed.includes('genesys')
              ? {
                  ...baseConfig,
                  ADD_TAGS: ['iframe', 'style'],
                  ADD_ATTR: ['allowfullscreen', 'scrolling', 'name'],
                }
              : baseConfig;

            const sanitized = DOMPurify.sanitize(dirty, config);

            if (DOMPurify.removed && DOMPurify.removed.length > 0) {
              console.warn(
                '[TrustedTypes] DOMPurify stripped potentially unsafe HTML elements/attributes:',
                {
                  originalHTMLLength: dirty.length,
                  removed: DOMPurify.removed.map(item => {
                    if (typeof item === 'object' && item !== null) {
                      const typedItem = item as {
                        element?: Element;
                        attribute?: Attr;
                      };
                      return (
                        typedItem.element?.outerHTML ??
                        typedItem.attribute?.name ??
                        String(item)
                      );
                    }
                    return String(item);
                  }),
                },
              );
            }

            return sanitized;
          },

          createScriptURL: (dirtyUrl: string) => {
            console.log(
              '[TrustedTypes] createScriptURL called for URL:',
              dirtyUrl,
            );
            for (const item of ALLOWLIST) {
              if (typeof item === 'string' && dirtyUrl.startsWith(item)) {
                return dirtyUrl;
              }
              if (item instanceof RegExp && item.test(dirtyUrl)) {
                return dirtyUrl;
              }
            }
            throw new Error(`Script URL not allowed: ${dirtyUrl}`);
          },

          /*
            Required for Qualtrics which uses dynamic inline JS.
            This weakens Trusted Types protection for script strings.
            External script origins remain locked by CSP.
          */
          createScript: (inlineScript: string) => {
            console.log(
              '[TrustedTypes] createScript called with content snippet:',
              inlineScript.slice(0, 100) +
                (inlineScript.length > 100 ? '...' : ''),
            );
            return inlineScript;
          },
        });
      } catch (error) {
        // Handle the error, e.g., log it or display an error message
        console.error('Error creating trusted types policy:', error);
      }
    }
  }, []);

  return null;
}
